import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  Appointment,
  LocationData,
  LocationDataGeocoded,
} from '@sc/data-access-models';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { MomentParseZonePipe } from '../../../pipes/parse-zone.pipe';
import { AppointmentLightNewComponent } from '../../appointment-light-new/appointment-light-new.component';
import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'sc-calendar-appointments-panel',
  styleUrls: ['./calendar-appointments-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    IconComponent,
    NgFor,
    NgClass,
    ButtonComponent,
    AppointmentLightNewComponent,
    AsyncPipe,
    TranslateModule,
    LocaleDatePipe,
    MomentParseZonePipe,
  ],
  template: `
    <div class="appointments-panel-header">
      <span *ngIf="!newAppointmentView">{{
        date | localeDate: 'fullDate' | async
      }}</span>
      <span *ngIf="newAppointmentView">{{
        'CalendarAppointmentsPanel.newAppointmentBtn' | translate
      }}</span>

      <sc-icon
        class="closeSlotCalendarButton"
        name="close"
        (click)="onClose()"
      ></sc-icon>
    </div>

    <div *ngIf="!newAppointmentView" class="appointments-panel-body">
      <div class="appointments-list">
        <div
          [class]="'appointment selected calendarSlotSelection' + index"
          [ngClass]="{ selected: isSelectedAppointment(appointment) }"
          *ngFor="let appointment of appointments; let index = index"
          (click)="onSelectAppointment(appointment)"
        >
          <span class="slot-garage-text">
            {{
              appointment.time
                | parseZoneTimeString: timezoneService.getTimezone(appointment)
                | localeDate: 'shortTime'
                | async
            }}</span
          >
          <span
            class="replacement-vehicle-icon"
            *ngIf="appointment.replacementVehicle"
          >
            <sc-icon name="car"></sc-icon>
          </span>
        </div>
      </div>

      <div
        class="information-panel"
        *ngIf="selectedAppointment && !disabledReplacementVehicle"
      >
        <p>
          {{
            'CalendarAppointmentsPanel.carImmoInfo'
              | translate: { duration: defaultDuration }
          }}
          {{
            selectedAppointment.replacementVehicle
              ? ('CalendarAppointmentsPanel.replacementCarPropose' | translate)
              : ''
          }}
        </p>
        <div
          class="confirmation-selction"
          *ngIf="selectedAppointment.replacementVehicle"
        >
          <button
            class="withReplacementVehicleCalendarSlotSelection"
            [ngClass]="{ selected: withReplacementVehicle }"
            (click)="withReplacementVehicle = true"
          >
            {{ 'CalendarAppointmentsPanel.yesBtn' | translate }}
          </button>

          <button
            class="withOutReplacementVehicleCalendarSlotSelection"
            [ngClass]="{ selected: !withReplacementVehicle }"
            (click)="withReplacementVehicle = false"
          >
            {{ 'CalendarAppointmentsPanel.noBtn' | translate }}
          </button>
        </div>
      </div>

      <sc-button
        class="validation-button"
        [disabled]="!selectedAppointment"
        buttonType="primary"
        (click)="onValidateSelection()"
      >
        {{ 'CalendarAppointmentsPanel.selectBtn' | translate }}
      </sc-button>

      <sc-button
        *ngIf="canCreateLightAppointment"
        class="new-apt-button"
        buttonType="mainWhite"
        (click)="newAppointmentView = !newAppointmentView"
      >
        {{ 'Créer un nouveau créneau' | translate }}
      </sc-button>
    </div>
    <sc-appointment-light-new
      *ngIf="newAppointmentView && canCreateLightAppointment"
      (returnEvent)="newAppointmentView = !newAppointmentView"
      [date]="date"
      [existingAppointment]="existingAppointment"
      [disabledReplacementVehicle]="disabledReplacementVehicle"
      [suggestedLocationData]="suggestedLocationData"
      [selectedGeocodedAddress]="selectedGeocodedAddress"
      (addressSelectEvent)="addressSelected($event)"
      (addressStrChange)="addressStrChange($event)"
      (lightAptValidetEvent)="onLightAptValidete($event)"
    ></sc-appointment-light-new>
  `,
})
export class CalendarAppointmentsPanelComponent {
  @Input() date: Date;
  @Input() appointments: Appointment[];
  @Input() existingAppointment: Appointment;
  @Input() disabledReplacementVehicle: boolean;
  @Input() defaultDuration: string;
  @Input() suggestedLocationData: LocationData[];
  @Input() selectedGeocodedAddress: LocationDataGeocoded;
  @Input() canCreateLightAppointment;

  @Output() closeClick = new EventEmitter();
  @Output() selectAppointment = new EventEmitter<Appointment>();
  @Output() addressSelect = new EventEmitter<string>();
  @Output() addressChange = new EventEmitter<LocationData>();
  @Output() lightAptValidetEvent = new EventEmitter<Appointment>();

  selectedAppointment: Appointment;
  withReplacementVehicle: boolean;
  newAppointmentView = false;

  constructor(public timezoneService: TimezoneService) {}

  isSelectedAppointment(appointment: Appointment) {
    return this.selectedAppointment === appointment;
  }

  onSelectAppointment(appointment: Appointment) {
    this.selectedAppointment =
      appointment !== this.selectedAppointment ? appointment : null;
  }

  onClose() {
    this.closeClick.emit();
  }

  onValidateSelection() {
    this.selectAppointment.emit({
      ...this.selectedAppointment,
      isReplacementVehicleSelected: this.withReplacementVehicle,
    });
  }

  addressSelected($event) {
    this.addressSelect.emit($event);
  }

  addressStrChange($event) {
    this.addressChange.emit($event);
  }

  onLightAptValidete(value) {
    this.lightAptValidetEvent.emit(value);
  }
}
